import {
  Calendar,
  CalendarResourceWithType,
  PersonWithType,
  RoomWithType,
} from "frontend/interfaces/calendar";
import { DayViewRaw } from "frontend/interfaces/day-view";
import { parseUnit } from "frontend/parser/parse-unit";
import { parsePerson } from "frontend/parser/parse-person";
import { Person } from "frontend/interfaces/person";
import { header, note } from "frontend/uses/abstract-view/keys-and-labels";
import { useAbstractViewParser } from "frontend/uses/abstract-view/use-abstract-view-parser";
import { useStore as useProfileStore } from "frontend/stores/profile";
import { parseRoom } from "frontend/parser/parse-room";
import { Room } from "frontend/interfaces/room";

export function useDayViewParser(date: Date, dayView: DayViewRaw): Calendar {
  const { sortPersons, filterPersons, sortRooms, filterRooms } =
    useProfileStore();

  const pivotPersonSelector = (persons: Person[]): PersonWithType[] => {
    return filterPersons(sortPersons(persons)).map((p) => ({
      ...p,
      type: "person",
    }));
  };

  const pivotRoomSelector = (rooms: Room[]): RoomWithType[] => {
    return filterRooms(sortRooms(rooms)).map((r) => ({
      ...r,
      type: "room",
    }));
  };

  return useAbstractViewParser<CalendarResourceWithType>(
    {
      units: dayView.units.map((unitRaw) => parseUnit(unitRaw, date)),
      persons: dayView.persons.map(parsePerson),
      rooms: dayView.rooms.map(parseRoom),
      dates: [date],
    },
    {
      calculateColumnPivots: (database) => {
        const persons = pivotPersonSelector(database.persons);
        const rooms = pivotRoomSelector(database.rooms);
        return [...persons, ...rooms];
      },
      calculateColumnHeader: (pivot, _database, _options) =>
        header(pivot.id, pivot.name),
      calculateColumnNote: (_person, _database, _options) => {
        return dayView.holidays.map((holiday) =>
          note(holiday.id, holiday.name)
        );
      },
      calculateCellAt: (
        resource,
        intervalReferenceDate,
        columnIntervalReferenceDate,
        _options
      ) => ({
        resource: resource,
        interval: intervalReferenceDate,
        columnInterval: columnIntervalReferenceDate,
        key: resource.id,
      }),
    },
    {}
  );
}
